import * as React from "react";
import { Button, Form, Modal, Loader } from "semantic-ui-react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { z } from "zod";
import { useForm, FormProvider } from "react-hook-form";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { zodResolver } from "@hookform/resolvers/zod";
import { AppState, OrgStructureEditModel } from "@teamprevent/common/orgStructure/models";
import { formatISO } from "date-fns";

import { saveOrgStructure } from "../actions";
import { SAVE_ORGSTRUCTURE } from "../actiontypes";
import { basePathSelectorRemove } from "../selectors";

import { Edit } from "./Edit";

import { push } from "redux-little-router";

const orgStructureSchema = z.object({
    name: z.string({ required_error: "Name is required" }),
    id: z.string().optional(),
    customerId: z.string().optional(),
    billing: z.boolean().optional(),
    financeCustomerName: z.string().nullish().optional(),
    accountCode: z.string().nullish().optional(),
    contractCode: z.string().nullish().optional(),
    allowanceStartDate: z.string().nullish().optional(),
    allowanceEndDate: z.string().nullish().optional(),
    purchaseOrderNumber: z.string().nullish().optional()
        .refine((val: string | null | undefined) => {
            if (!val || val.length >= 4) {
                return true;
            }

            return false;
        }, { message: "Purchase Order Number should have at-least 4 characters" }),
});

export const EditModal = () => {
    const dispatch = useDispatch();

    const open = useSelector<AppState, boolean>(state => state.router.pathname?.endsWith("/edit") === true);
    const basePath = useSelector<AppState, string | undefined>(state => basePathSelectorRemove(state, `/${state.orgStructure.id}/edit`));
    const customerId = useSelector<AppState, string>(state => state.router.params?.customerId ?? "");
    const model = useSelector<AppState, OrgStructureEditModel>(({ orgStructure }) => ({
        billing: false,
        ...orgStructure,
        allowanceStartDate: orgStructure.allowanceStartDate ? formatISO(orgStructure.allowanceStartDate, { representation: "date" }) : undefined,
        allowanceEndDate: orgStructure.allowanceEndDate ? formatISO(orgStructure.allowanceEndDate, { representation: "date" }) : undefined,
    }));

    const close = React.useCallback(() => {
        if (basePath) {
            dispatch(push(basePath));
        }
    }, [basePath, dispatch]);

    const save = (orgStructure: OrgStructureEditModel) => dispatch(saveOrgStructure(customerId, orgStructure, basePath));
    const { handleSubmit, ...methods } = useForm<OrgStructureEditModel>({
        resolver: zodResolver(orgStructureSchema),
        values: model,
        defaultValues: {
            financeCustomerName: undefined,
            ...model
        }
    });

    return (
        <Modal size="small" as={Form} onSubmit={handleSubmit(save)} open={open} onClose={close} closeOnDimmerClick={false}>
            <FormProvider handleSubmit={handleSubmit} {...methods}>
                <Modal.Header>Edit Org Structure</Modal.Header>
                <Modal.Content>
                    {model.id === "-1" ? <Loader /> : <Edit />}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={close}>Cancel</Button>
                    <IsLoadingButton requestNames={SAVE_ORGSTRUCTURE} content="Save" type="submit" />
                </Modal.Actions>
            </FormProvider>
        </Modal>
    );
};
